import { useEffect, useRef, useState } from 'react'
import { Button, Paper, Title, Text, Stack, Center, Box, Alert, TextInput, CopyButton } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle, IconCircleCheck, IconCopy, IconCheck } from '@tabler/icons-react'
import type { PasskeyCredentialSummary } from '@paperair/shared-types'
import { authClient } from '../../api/authClient'
import { getErrorMessage } from '../../api/client'

interface PasskeyConfirmViewProps {
  token: string
  onDone: () => void
}

type Status = 'running' | 'unsupported' | 'error' | 'done'

/**
 * Lands here from the enrollment confirmation email link (`/passkeys/confirm?token=...`).
 * Build plan §4.1: the ceremony must start from this page load's user gesture (opening the
 * link), so it runs immediately rather than waiting for another click. The server-derived
 * default name (from the AAGUID map / User-Agent) only exists once the credential is
 * actually created, so "name it" is an immediate rename-in-place after success rather than
 * an input collected up front.
 */
export function PasskeyConfirmView({ token, onDone }: PasskeyConfirmViewProps) {
  const [status, setStatus] = useState<Status>('running')
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<PasskeyCredentialSummary | null>(null)
  const [name, setName] = useState('')
  const [isSavingName, setIsSavingName] = useState(false)
  const startedRef = useRef(false)

  const runCeremony = async () => {
    setStatus('running')
    setError(null)
    try {
      const passkey = await authClient.passkeys.completeEnrollment(token)
      setCreated(passkey)
      setName(passkey.name)
      setStatus('done')
    } catch (err) {
      setError(getErrorMessage(err))
      setStatus('error')
    }
  }

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    if (!authClient.passkeys.isSupported()) {
      setStatus('unsupported')
      return
    }
    runCeremony()
    // Mount-only, guarded by startedRef — runCeremony is intentionally not a dependency.
  }, [])

  const handleSaveName = async () => {
    if (!created || !name.trim() || name === created.name) return
    setIsSavingName(true)
    try {
      const updated = await authClient.passkeys.rename(created.id, name.trim())
      setCreated(updated)
      setName(updated.name)
      notifications.show({ title: 'Saved', message: 'Passkey renamed', color: 'green' })
    } catch (err) {
      notifications.show({ title: 'Error', message: getErrorMessage(err), color: 'red' })
    } finally {
      setIsSavingName(false)
    }
  }

  const confirmUrl = `${window.location.origin}/passkeys/confirm?token=${token}`

  return (
    <Center style={{ minHeight: '100vh' }}>
      <Box w={440}>
        <Title ta="center" mb="md">
          Add a passkey
        </Title>

        <Paper withBorder shadow="md" p="xl" radius="md">
          {status === 'running' && (
            <Stack align="center" py="md">
              <Text ta="center">Follow your browser's prompt to finish adding your passkey…</Text>
            </Stack>
          )}

          {status === 'unsupported' && (
            <Stack>
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="Can't add a passkey here">
                This browser doesn't support passkeys — that's common inside an email app's built-in browser (Gmail,
                Outlook). Open this link in Safari or Chrome to continue.
              </Alert>
              <CopyButton value={confirmUrl}>
                {({ copied, copy }) => (
                  <Button
                    variant="default"
                    leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    onClick={copy}
                  >
                    {copied ? 'Link copied' : 'Copy link'}
                  </Button>
                )}
              </CopyButton>
              <Text size="xs" c="dimmed" ta="center">
                This link stays valid for the full confirmation window, so it's fine to open it again elsewhere.
              </Text>
            </Stack>
          )}

          {status === 'error' && (
            <Stack>
              <Alert icon={<IconAlertCircle size={16} />} color="red" title="Couldn't add your passkey">
                {error}
              </Alert>
              <Button onClick={runCeremony}>Try again</Button>
            </Stack>
          )}

          {status === 'done' && created && (
            <Stack>
              <Stack align="center" gap="xs">
                <IconCircleCheck size={40} color="var(--mantine-color-green-6)" />
                <Text fw={500}>Passkey added</Text>
              </Stack>
              <TextInput
                label="Name this passkey"
                maxLength={64}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                onBlur={handleSaveName}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                disabled={isSavingName}
              />
              <Button onClick={onDone}>Done</Button>
            </Stack>
          )}
        </Paper>
      </Box>
    </Center>
  )
}
